// Copyright (c) 2026 IOTA Stiftung
// SPDX-License-Identifier: Apache-2.0

import cx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { GLOBAL_DATE_TYPE, type DateFormat, type DateType } from '~/contexts/dateFormatContext';
import { useFormattedDate } from '~/hooks/useFormattedDate';

const TOOLTIP_DURATION_MS = 1_000;

const FORMAT_LABEL: Record<DateFormat, string> = {
    default: 'Relative time',
    local: 'Local time',
    utc: 'UTC',
};

interface DateDisplayProps {
    timestamp: number | string;
    type?: DateType;
    showTimeAgo?: boolean;
    showTooltip?: boolean;
    showHoverStyle?: boolean;
    relativeTimeHelperText?: string;
    relativeText?: string;
    allowWrap?: boolean;
}

export function DateDisplay({
    timestamp,
    type,
    showTimeAgo = false,
    showTooltip = true,
    showHoverStyle = true,
    relativeTimeHelperText,
    relativeText,
    allowWrap = false,
}: DateDisplayProps): JSX.Element {
    const effectiveType = type ?? GLOBAL_DATE_TYPE;
    const timestampMs = Number(timestamp);
    const { displayed, format, cycle } = useFormattedDate(effectiveType, timestampMs, showTimeAgo);

    const [showMessage, setShowMessage] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => () => clearTimeout(timeoutRef.current), []);

    function handleClick() {
        cycle();
        clearTimeout(timeoutRef.current);

        if (showTooltip) {
            setShowMessage(true);
            timeoutRef.current = setTimeout(() => setShowMessage(false), TOOLTIP_DURATION_MS);
        }
    }

    function handleKeyDown(e: React.KeyboardEvent) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
        }
    }

    const timeElement = (
        <time
            dateTime={new Date(timestampMs).toISOString()}
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            className={cx(
                'cursor-pointer select-none',
                showHoverStyle &&
                    'rounded-md p-1 hover:bg-iota-neutral-96 dark:hover:bg-iota-neutral-12',
                showHoverStyle && (allowWrap ? 'inline-block' : 'text-nowrap'),
            )}
        >
            {format === 'default' && relativeText ? relativeText : displayed}

            {format === 'default' && !relativeText && relativeTimeHelperText ? (
                <span> {relativeTimeHelperText}</span>
            ) : null}
        </time>
    );

    if (!showTooltip) {
        return timeElement;
    }

    return (
        <span className="relative inline-block">
            {showMessage && (
                <span className="tooltip-bg tooltip-text-color absolute bottom-full left-1/2 mb-1 -translate-x-1/2 transform whitespace-nowrap rounded p-xs text-label-sm">
                    {FORMAT_LABEL[format]}
                </span>
            )}
            {timeElement}
        </span>
    );
}
